import type {
  IChartApi,
  IPanePrimitive,
  IPanePrimitivePaneView,
  IPrimitivePaneRenderer,
  ISeriesApi,
  PrimitivePaneViewZOrder,
  SeriesType,
  Time,
  UTCTimestamp,
} from 'lightweight-charts';

/**
 * The canvas target handed to a renderer.
 *
 * Derived from the library's own interface rather than imported from
 * `fancy-canvas`. That package is a transitive dependency of lightweight-charts,
 * not a direct one, so importing it would either fail to resolve or pin us to a
 * version the chart library did not choose. Reading the type off the signature
 * keeps it correct across upgrades for free.
 */
type RenderTarget = Parameters<NonNullable<IPrimitivePaneRenderer['drawBackground']>>[0];

export interface WindowBand {
  /** Stored time of the first bar in the band. */
  from: number;
  /** Stored time of the last bar in the band. */
  to: number;
  color: string;
  /** Window name, drawn at the band's start edge. Omitted if not wanted. */
  label?: string;
  /** Projected ahead of price rather than measured. Drawn with an outline. */
  future?: boolean;
  /**
   * The window's own high and low.
   *
   * Given both, the band is drawn as a BOX spanning them — the way the
   * MetaTrader indicator draws a session — so the shading says where price
   * traded and not merely when the window ran. Without them it falls back to
   * a full-height band, which is right for a projected occurrence: it has not
   * happened yet, so it has no range to show.
   */
  high?: number;
  low?: number;
}

/** A single moment, drawn as a dashed vertical line. */
export interface PointMarker {
  /** Stored time of the bar that carries the point. */
  at: number;
  color: string;
  /** Point name, drawn up from the foot of its line. Omitted if not wanted. */
  label?: string;
  /** Projected ahead of price rather than measured. */
  future?: boolean;
}

/**
 * Draws shaded vertical bands over a chart pane.
 *
 * Implemented as a pane primitive rather than by faking it with a series.
 * Lightweight Charts has no vertical-span series, and the usual workarounds — a
 * histogram scaled to the pane height, or an area series pinned to the top —
 * distort the price scale and fight autoscaling. A primitive draws straight to
 * the canvas and is invisible to the scale entirely.
 *
 * Rendering happens in `drawBackground`, which the library documents for exactly
 * this ("time areas highlighting"): it runs beneath the series, so candles stay
 * fully legible on top of the shading rather than being tinted by it.
 */
export class TimeWindowPrimitive implements IPanePrimitive<Time> {
  private chart: IChartApi | null = null;
  /*
   * The price series, handed in rather than taken from `attached`.
   *
   * This is a PANE primitive, and a pane knows nothing about the series drawn
   * on it — so there is no series in the attach parameter to read a price
   * coordinate from. The chart that owns both passes it along instead.
   */
  private series: ISeriesApi<SeriesType> | null = null;
  private requestUpdate: (() => void) | null = null;
  private bands: WindowBand[] = [];
  private points: PointMarker[] = [];
  private readonly view: IPanePrimitivePaneView;

  constructor(bands: WindowBand[] = [], points: PointMarker[] = []) {
    this.bands = bands;
    this.points = points;

    const renderer: IPrimitivePaneRenderer = {
      drawBackground: (target: RenderTarget) => this.render(target),
      // `draw` is required by the interface; everything is painted in the
      // background layer, so there is deliberately nothing to do here.
      draw: () => {},
    };

    this.view = {
      zOrder: (): PrimitivePaneViewZOrder => 'bottom',
      renderer: () => renderer,
    };
  }

  attached(param: { chart: IChartApi; requestUpdate: () => void }): void {
    this.chart = param.chart;
    this.requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this.chart = null;
    this.requestUpdate = null;
  }

  paneViews(): readonly IPanePrimitivePaneView[] {
    return [this.view];
  }

  /** Called by the chart once the candlestick series exists. */
  setSeries(series: ISeriesApi<SeriesType> | null): void {
    this.series = series;
    this.requestUpdate?.();
  }

  setBands(bands: WindowBand[]): void {
    this.bands = bands;
    this.requestUpdate?.();
  }

  setPoints(points: PointMarker[]): void {
    this.points = points;
    this.requestUpdate?.();
  }

  private render(target: RenderTarget): void {
    const chart = this.chart;
    if (!chart || (this.bands.length === 0 && this.points.length === 0)) return;

    const timeScale = chart.timeScale();

    target.useBitmapCoordinateSpace(
      ({ context, bitmapSize, horizontalPixelRatio, verticalPixelRatio }) => {
      for (const band of this.bands) {
        const x1 = timeScale.timeToCoordinate(band.from as UTCTimestamp);
        const x2 = timeScale.timeToCoordinate(band.to as UTCTimestamp);
        // null means the bar is scrolled out of view on this side.
        if (x1 === null || x2 === null) continue;

        const barWidth = timeScale.options().barSpacing;
        // Extend half a bar past each edge so the band covers the full candles
        // at its boundaries rather than stopping at their centres.
        const left = (x1 - barWidth / 2) * horizontalPixelRatio;
        const right = (x2 + barWidth / 2) * horizontalPixelRatio;

        const width = Math.max(right - left, horizontalPixelRatio);

        /*
         * A measured occurrence becomes a box across its own high and low.
         *
         * Falls back to shading the full pane height when the range is not
         * known — a projected window, or one whose prices are off the visible
         * scale — so the band never silently disappears.
         */
        const series = this.series;
        const yHigh =
          band.high === undefined ? null : (series?.priceToCoordinate(band.high) ?? null);
        const yLow = band.low === undefined ? null : (series?.priceToCoordinate(band.low) ?? null);

        context.fillStyle = band.color;
        if (yHigh !== null && yLow !== null) {
          const top = yHigh * verticalPixelRatio;
          const height = Math.max(yLow * verticalPixelRatio - top, verticalPixelRatio);
          // A small radius, clamped so a narrow or shallow box does not turn
          // into a lozenge.
          const radius = Math.min(3 * horizontalPixelRatio, width / 2, height / 2);
          context.beginPath();
          if (typeof context.roundRect === 'function') {
            context.roundRect(left, top, width, height, radius);
          } else {
            context.rect(left, top, width, height);
          }
          context.fill();
        } else {
          context.fillRect(left, 0, width, bitmapSize.height);
        }

        /*
         * A projected band gets edge rules as well as fill.
         *
         * Fill alone reads as "this happened"; the boundary lines make a future
         * window look like a plan rather than a measurement, which matters when
         * the two sit side by side either side of the last candle.
         */
        if (band.future) {
          context.save();
          context.strokeStyle = band.color;
          context.globalAlpha = 0.9;
          context.lineWidth = Math.max(1, Math.round(horizontalPixelRatio));
          context.setLineDash([4 * horizontalPixelRatio, 4 * horizontalPixelRatio]);
          for (const edge of [left, left + width]) {
            const px = Math.round(edge) + 0.5;
            context.beginPath();
            context.moveTo(px, 0);
            context.lineTo(px, bitmapSize.height);
            context.stroke();
          }
          context.restore();
        }
      }

      // Points sit on top of the bands: a point often marks the edge of a
      // window, and drawing it underneath would hide it inside the shading.
      for (const point of this.points) {
        const x = timeScale.timeToCoordinate(point.at as UTCTimestamp);
        if (x === null) continue;

        context.save();
        context.beginPath();
        context.strokeStyle = point.color;
        context.lineWidth = Math.max(1, Math.round(horizontalPixelRatio));
        // Dashes scaled by pixel ratio so they look the same on a HiDPI screen.
        context.globalAlpha = point.future ? 0.65 : 1;
        context.setLineDash([6 * horizontalPixelRatio, 5 * horizontalPixelRatio]);
        // +0.5 device pixels puts the stroke on the pixel centre instead of
        // straddling two, which is what makes a 1px vertical line look blurry.
        const px = Math.round(x * horizontalPixelRatio) + 0.5;
        context.moveTo(px, 0);
        context.lineTo(px, bitmapSize.height);
        context.stroke();
        context.restore();
      }
    });

    this.renderLabels(target);
  }

  /**
   * Window names at each band's start edge, and point names at the foot of
   * each line.
   *
   * Drawn in MEDIA coordinate space rather than bitmap space: media space is CSS
   * pixels, so a font size means what it says and text stays the same visual
   * size on a HiDPI screen without hand-scaling every metric.
   *
   * Both kinds share one media pass. Each pass re-enters the canvas and resets
   * its state, so splitting them would double that work every frame — and these
   * run on pan and zoom, not just on data changes.
   */
  private renderLabels(target: RenderTarget): void {
    const chart = this.chart;
    if (!chart) return;

    const labelled = this.bands.filter((b) => b.label);
    const labelledPoints = this.points.filter((p) => p.label);
    if (labelled.length === 0 && labelledPoints.length === 0) return;

    const timeScale = chart.timeScale();

    target.useMediaCoordinateSpace(({ context, mediaSize }) => {
      context.save();
      context.font =
        '600 10px ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
      context.textBaseline = 'top';

      // Track the right edge of each label already drawn, so overlapping bands
      // on a zoomed-out chart stack downward instead of printing on top of one
      // another into an unreadable smear.
      const rowEnds: number[] = [];
      const rowHeight = 14;

      for (const band of labelled) {
        const x1 = timeScale.timeToCoordinate(band.from as UTCTimestamp);
        if (x1 === null) continue;

        const x2 = timeScale.timeToCoordinate(band.to as UTCTimestamp);
        const barWidth = timeScale.options().barSpacing;
        const left = x1 - barWidth / 2;
        const right = x2 === null ? left : x2 + barWidth / 2;

        // A band only a few pixels wide has nowhere to put a name; drawing one
        // would just overlap its neighbours.
        if (right - left < 26) continue;

        const text = band.label!;
        const padding = 4;
        const maxWidth = right - left - padding * 2;
        if (maxWidth < 18) continue;

        const shown = fitText(context, text, maxWidth);
        const width = context.measureText(shown).width;

        let row = 0;
        while (row < rowEnds.length && (rowEnds[row] ?? 0) > left) row++;
        rowEnds[row] = left + width + padding * 2 + 4;

        const y = 4 + row * rowHeight;
        // Stop stacking once it would run into the chart body.
        if (y + rowHeight > mediaSize.height / 2) continue;

        // A chip behind the text so it stays readable over candles and grid.
        context.fillStyle = 'rgba(0, 0, 0, 0.55)';
        roundedRect(context, left + 1, y, width + padding * 2, 13, 3);
        context.fill();

        /*
         * White, not the band colour.
         *
         * `band.color` carries the importance alpha that shades the band itself
         * — 8% for medium, 24% for high — which is right for a fill spanning
         * hundreds of pixels and hopeless for 10px text. The colour identity is
         * already carried by the band under the label, so the name only has to
         * be legible.
         */
        context.fillStyle = '#ffffff';
        context.fillText(shown, left + 1 + padding, y + 2);
      }

      /*
       * Point names run UP from the foot of their own line.
       *
       * Rotated rather than horizontal because these lines repeat daily: a year
       * of H1 carries thousands of them, and horizontal names would collide into
       * a smear as soon as the chart is zoomed out past a couple of days.
       * Rotated, a name occupies only the ~13px of width its line already does,
       * so the labels stay readable at every zoom and simply thin out when the
       * lines crowd together.
       */
      const onScreen: { x: number; marker: PointMarker }[] = [];
      for (const point of labelledPoints) {
        const x = timeScale.timeToCoordinate(point.at as UTCTimestamp);
        if (x === null || x < 0 || x > mediaSize.width) continue;
        onScreen.push({ x, marker: point });
      }
      // Sorted so the thinning below keeps an even spread across the pane
      // rather than whichever points happened to come first in the array.
      onScreen.sort((a, b) => a.x - b.x);

      context.textBaseline = 'middle';
      context.textAlign = 'left';
      // Names may climb 45% of the pane before they start competing with price.
      const maxLength = mediaSize.height * 0.45;
      let lastX = Number.NEGATIVE_INFINITY;

      for (const { x, marker } of onScreen) {
        // Closer than this and the chips would touch, which reads worse than
        // showing fewer names.
        if (x - lastX < 15) continue;
        lastX = x;

        const shown = fitText(context, marker.label!, maxLength);
        const width = context.measureText(shown).width;
        const padding = 4;

        context.save();
        // Matches the line's own alpha so a projected point reads as a plan.
        context.globalAlpha = marker.future ? 0.75 : 1;
        // Origin at the foot of the line, then rotate so +x runs up the screen.
        context.translate(x + 3, mediaSize.height - 6);
        context.rotate(-Math.PI / 2);

        context.fillStyle = 'rgba(0, 0, 0, 0.55)';
        roundedRect(context, -padding, -6.5, width + padding * 2, 13, 3);
        context.fill();

        context.fillStyle = '#ffffff';
        context.fillText(shown, 0, 0);
        context.restore();
      }

      context.restore();
    });
  }
}

/** Truncate with an ellipsis so a long name degrades instead of overflowing. */
function fitText(context: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (context.measureText(text).width <= maxWidth) return text;

  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (context.measureText(`${text.slice(0, mid)}…`).width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return lo > 0 ? `${text.slice(0, lo)}…` : '';
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + w, y, x + w, y + h, r);
  context.arcTo(x + w, y + h, x, y + h, r);
  context.arcTo(x, y + h, x, y, r);
  context.arcTo(x, y, x + w, y, r);
  context.closePath();
}
